/**
 * COMISSÃO E QUITAÇÃO NO APARELHO (tarefa 2D) — e a fila delas.
 *
 * Não moram dentro da ficha porque o dono é outro: a ficha é do comprador; a
 * comissão e o pagamento efetivado são do gestor (no banco, tabelas próprias
 * com regra própria). Aqui ficam guardados por pessoa, como o armazém: a
 * chave leva o id de quem entrou. O comprador nunca recebe comissão do
 * servidor, então ela não existe no aparelho dele.
 *
 * SEM SINAL FUNCIONA: o gestor lança no celular, fica pendente, e sobe no
 * «Sincronizar» junto com as fichas (depois delas — a comissão e a quitação
 * apontam para a ficha no banco). Nada é apagado: quitação errada é ANULADA
 * com motivo e continua na lista.
 *
 * 3B: a «comissão a receber (cliente)» mora aqui também, pelo mesmo motivo
 * (é do gestor, e o comprador nunca a recebe), com fila e recusa iguais.
 *
 * 3F: a tabela de venda da porteira Revenda (preço ao comprador final, lote a
 * lote) e os recebimentos dele também moram aqui — mesmo dono (o gestor),
 * mesma regra (o comprador nunca recebe, nem no aparelho), mesma fila. O
 * preço de venda NUNCA vai para dentro da ficha: a ficha sincroniza para o
 * comprador. Campos novos e opcionais no que está guardado: o `completar`
 * preenche vazio, e a chave não muda.
 *
 * RECUSA DE VEZ (ex.: comissão já acertada, 403, 400): sai da fila, senão o
 * contador nunca zera e a versão nova do app nunca é oferecida. A frase fica
 * na lista de recusas do «Sincronizar». A comissão e a anulação voltam ao que
 * o banco tem (a próxima descida vem inteira); a quitação, que nunca entrou
 * no banco, fica aqui riscada como «recusada».
 *
 * R2B (migração 014): o recebimento da comissão do cliente e o pagamento da
 * comissão do comprador fora do acerto moram aqui também — mesmo dono (gestor
 * e Consulta dão baixa), mesma fila, mesmo jeito do recebimento da revenda.
 * Campos novos e opcionais: o `completar` preenche vazio, e a chave não muda.
 *
 * Ao mudar o formato dos dados guardados, mudar a versão da CHAVE.
 */
import { useSyncExternalStore } from 'react'
import { agora, novoId, reais } from '../dominio/numeros'
import type { Comissao, ComissaoCliente, Comprovante, LoteDeVenda, PagamentoDeComissao, Quitacao, Recebimento, VendaDaPorteira } from '../dominio/tipos'
import { lerDono, lerEstado } from './armazem'
import { api, Recusa } from './sessao'

const prefixo = 'pesagem.pagamentos.v1.'

export interface EstadoDePagamentos {
  /** por id da ficha */
  comissoes: Record<string, Comissao>
  quitacoes: Quitacao[]
  /**
   * 3B · «comissão a receber (cliente)», por chave `fichaId|destinoId` (uma por
   * porteira de cliente). Campo novo e opcional no que está guardado: o
   * `completar` preenche vazio, e o aparelho antigo não perde nada.
   */
  comissoesCliente: Record<string, ComissaoCliente>
  /** 3F · tabela de venda de cada porteira Revenda, pela mesma chave `fichaId|destinoId` */
  vendas: Record<string, VendaDaPorteira>
  /** 3F · o que o comprador final pagou ao gestor */
  recebimentos: Recebimento[]
  /** R2B · o que o cliente pagou da comissão (014) */
  recebimentosComissao: Recebimento[]
  /** R2B · a comissão do comprador paga fora do acerto (014) */
  pagamentosComissao: PagamentoDeComissao[]
  /** o que o gestor lançou aqui e ainda não subiu */
  pendentes: {
    comissoes: string[]; quitacoes: string[]; anulacoes: string[]; comissoesCliente: string[]
    /** 3F · chaves das tabelas de venda, ids dos recebimentos e das anulações deles */
    vendas: string[]; recebimentos: string[]; anulacoesRecebimento: string[]
    /** R2B · os da 014 e as anulações deles */
    recebimentosComissao: string[]; anulacoesRecebimentoComissao: string[]
    pagamentosComissao: string[]; anulacoesPagamentoComissao: string[]
  }
  ultimaSync: string | null
}

function vazio(): EstadoDePagamentos {
  return {
    comissoes: {}, quitacoes: [], comissoesCliente: {}, vendas: {}, recebimentos: [], recebimentosComissao: [], pagamentosComissao: [],
    pendentes: {
      comissoes: [], quitacoes: [], anulacoes: [], comissoesCliente: [], vendas: [], recebimentos: [], anulacoesRecebimento: [],
      recebimentosComissao: [], anulacoesRecebimentoComissao: [], pagamentosComissao: [], anulacoesPagamentoComissao: [],
    },
    ultimaSync: null,
  }
}

function completar(e: Partial<EstadoDePagamentos> | null): EstadoDePagamentos {
  const v = vazio()
  if (!e || typeof e !== 'object') return v
  return {
    comissoes: e.comissoes && typeof e.comissoes === 'object' ? e.comissoes : {},
    quitacoes: Array.isArray(e.quitacoes) ? e.quitacoes : [],
    comissoesCliente: e.comissoesCliente && typeof e.comissoesCliente === 'object' ? e.comissoesCliente : {},
    vendas: e.vendas && typeof e.vendas === 'object' ? e.vendas : {},
    recebimentos: Array.isArray(e.recebimentos) ? e.recebimentos : [],
    recebimentosComissao: Array.isArray(e.recebimentosComissao) ? e.recebimentosComissao : [],
    pagamentosComissao: Array.isArray(e.pagamentosComissao) ? e.pagamentosComissao : [],
    pendentes: {
      comissoes: Array.isArray(e.pendentes?.comissoes) ? e.pendentes.comissoes : [],
      quitacoes: Array.isArray(e.pendentes?.quitacoes) ? e.pendentes.quitacoes : [],
      anulacoes: Array.isArray(e.pendentes?.anulacoes) ? e.pendentes.anulacoes : [],
      comissoesCliente: Array.isArray(e.pendentes?.comissoesCliente) ? e.pendentes.comissoesCliente : [],
      vendas: Array.isArray(e.pendentes?.vendas) ? e.pendentes.vendas : [],
      recebimentos: Array.isArray(e.pendentes?.recebimentos) ? e.pendentes.recebimentos : [],
      anulacoesRecebimento: Array.isArray(e.pendentes?.anulacoesRecebimento) ? e.pendentes.anulacoesRecebimento : [],
      recebimentosComissao: Array.isArray(e.pendentes?.recebimentosComissao) ? e.pendentes.recebimentosComissao : [],
      anulacoesRecebimentoComissao: Array.isArray(e.pendentes?.anulacoesRecebimentoComissao) ? e.pendentes.anulacoesRecebimentoComissao : [],
      pagamentosComissao: Array.isArray(e.pendentes?.pagamentosComissao) ? e.pendentes.pagamentosComissao : [],
      anulacoesPagamentoComissao: Array.isArray(e.pendentes?.anulacoesPagamentoComissao) ? e.pendentes.anulacoesPagamentoComissao : [],
    },
    ultimaSync: e.ultimaSync ?? null,
  }
}

let donoAberto: string | null = null
let estado = vazio()
const ouvintes = new Set<() => void>()

/**
 * Abre os dados de quem entrou. Preguiçoso de propósito: o armazém decide o
 * dono ao entrar, e aqui só se confere se mudou — sem mexer no login.
 */
function garantirDono(): void {
  const id = lerDono()?.id ?? null
  if (id === donoAberto) return
  donoAberto = id
  try {
    const t = id ? localStorage.getItem(prefixo + id) : null
    estado = completar(t ? JSON.parse(t) : null)
  } catch {
    estado = vazio()
  }
}

export function usarPagamentos(): EstadoDePagamentos {
  return useSyncExternalStore(
    (f) => { ouvintes.add(f); return () => ouvintes.delete(f) },
    () => { garantirDono(); return estado },
  )
}

export function lerPagamentos(): EstadoDePagamentos {
  garantirDono()
  return estado
}

function mudar(fn: (e: EstadoDePagamentos) => void): void {
  garantirDono()
  const novo = structuredClone(estado)
  fn(novo)
  estado = novo
  try { if (donoAberto) localStorage.setItem(prefixo + donoAberto, JSON.stringify(estado)) } catch { /* sem disco: a tela do armazém já avisa */ }
  ouvintes.forEach((f) => f())
}

const juntar = (lista: string[], id: string) => (lista.includes(id) ? lista : [...lista, id])

/* ---------- o que as telas usam ---------- */

export function quitacoesDa(e: EstadoDePagamentos, fichaId: string): Quitacao[] {
  return e.quitacoes.filter((q) => q.fichaId === fichaId).sort((a, b) => (a.pagoEm + a.lancadaEm).localeCompare(b.pagoEm + b.lancadaEm))
}

export function quantosPendentes(e: EstadoDePagamentos): number {
  return e.pendentes.comissoes.length + e.pendentes.quitacoes.length + e.pendentes.anulacoes.length
    + e.pendentes.comissoesCliente.length
    + e.pendentes.vendas.length + e.pendentes.recebimentos.length + e.pendentes.anulacoesRecebimento.length
    + e.pendentes.recebimentosComissao.length + e.pendentes.anulacoesRecebimentoComissao.length
    + e.pendentes.pagamentosComissao.length + e.pendentes.anulacoesPagamentoComissao.length
}

/* ---------- R2B · recebimento da comissão do cliente e pagamento da comissão (014) ---------- */

/** Os recebimentos da comissão de uma porteira de cliente, em ordem de data. */
export function recebimentosDaComissao(e: EstadoDePagamentos, fichaId: string, destinoId: string): Recebimento[] {
  return e.recebimentosComissao.filter((x) => x.fichaId === fichaId && x.destinoId === destinoId)
    .sort((a, b) => (a.recebidoEm + a.lancadoEm).localeCompare(b.recebidoEm + b.lancadoEm))
}

/** Os pagamentos da comissão do comprador fora do acerto, em ordem de data. */
export function pagamentosDaComissao(e: EstadoDePagamentos, fichaId: string): PagamentoDeComissao[] {
  return e.pagamentosComissao.filter((x) => x.fichaId === fichaId)
    .sort((a, b) => (a.pagoEm + a.lancadoEm).localeCompare(b.pagoEm + b.lancadoEm))
}

/** Gestor ou Consulta (é «baixa»): a tela confere, e o banco também. */
export function lancarRecebimentoComissao(x: Pick<Recebimento, 'fichaId' | 'destinoId' | 'valor' | 'recebidoEm' | 'obs'>): string {
  const id = novoId()
  mudar((e) => {
    e.recebimentosComissao.push({ ...x, id, lancadoEm: agora(), anuladoEm: null, anuladoMotivo: '', comprovantes: [], lancadoPorNome: lerDono()?.nome ?? '' })
    e.pendentes.recebimentosComissao = juntar(e.pendentes.recebimentosComissao, id)
  })
  return id
}

export function anularRecebimentoComissao(id: string, motivo: string): void {
  mudar((e) => {
    const x = e.recebimentosComissao.find((y) => y.id === id)
    if (!x || x.anuladoEm || x.recusado) return
    x.anuladoEm = agora(); x.anuladoMotivo = motivo; x.anuladoPorNome = lerDono()?.nome ?? ''
    e.pendentes.anulacoesRecebimentoComissao = juntar(e.pendentes.anulacoesRecebimentoComissao, id)
  })
}

export function anexarNoRecebimentoComissao(id: string, novos: Comprovante[]): void {
  mudar((e) => { const x = e.recebimentosComissao.find((y) => y.id === id); if (x) x.comprovantes.push(...novos) })
}

export function lancarPagamentoComissao(x: Pick<PagamentoDeComissao, 'fichaId' | 'valor' | 'pagoEm' | 'obs' | 'contaOrigem'>): string {
  const id = novoId()
  mudar((e) => {
    e.pagamentosComissao.push({ ...x, id, lancadoEm: agora(), anuladoEm: null, anuladoMotivo: '', comprovantes: [], lancadoPorNome: lerDono()?.nome ?? '' })
    e.pendentes.pagamentosComissao = juntar(e.pendentes.pagamentosComissao, id)
  })
  return id
}

export function anularPagamentoComissao(id: string, motivo: string): void {
  mudar((e) => {
    const x = e.pagamentosComissao.find((y) => y.id === id)
    if (!x || x.anuladoEm || x.recusado) return
    x.anuladoEm = agora(); x.anuladoMotivo = motivo; x.anuladoPorNome = lerDono()?.nome ?? ''
    e.pendentes.anulacoesPagamentoComissao = juntar(e.pendentes.anulacoesPagamentoComissao, id)
  })
}

export function anexarNoPagamentoComissao(id: string, novos: Comprovante[]): void {
  mudar((e) => { const x = e.pagamentosComissao.find((y) => y.id === id); if (x) x.comprovantes.push(...novos) })
}

/** 3B · a chave da comissão a receber: uma por porteira de cliente de cada ficha. */
export function chaveDoCliente(fichaId: string, destinoId: string): string {
  return `${fichaId}|${destinoId}`
}

/** 3B · a comissão a receber (cliente) de uma porteira, como o fechamento a enxerga. */
export function comissaoDoCliente(e: EstadoDePagamentos, fichaId: string, destinoId: string): ComissaoCliente | null {
  return e.comissoesCliente[chaveDoCliente(fichaId, destinoId)] ?? null
}

/** 3B · só o gestor chega aqui (a tela não mostra para os outros, e o banco recusa). */
export function lancarComissaoCliente(fichaId: string, destinoId: string, porCabeca: number, obs: string): void {
  const k = chaveDoCliente(fichaId, destinoId)
  mudar((e) => {
    e.comissoesCliente[k] = { fichaId, destinoId, porCabeca, obs, lancadaEm: agora() }
    e.pendentes.comissoesCliente = juntar(e.pendentes.comissoesCliente, k)
  })
}

/* ---------- 3F · revenda: preço de venda e recebimentos ---------- */

/** A tabela de venda de uma porteira Revenda (só existe no aparelho do gestor e da Consulta). */
export function vendaDaPorteira(e: EstadoDePagamentos, fichaId: string, destinoId: string): VendaDaPorteira | null {
  return e.vendas[chaveDoCliente(fichaId, destinoId)] ?? null
}

/** Só o gestor chega aqui (a tela não mostra para os outros, e o banco recusa). */
export function lancarVenda(fichaId: string, destinoId: string, lotes: LoteDeVenda[], obs: string): void {
  const k = chaveDoCliente(fichaId, destinoId)
  mudar((e) => {
    e.vendas[k] = { fichaId, destinoId, lotes: structuredClone(lotes), obs, lancadaEm: agora() }
    e.pendentes.vendas = juntar(e.pendentes.vendas, k)
  })
}

/** Os recebimentos de uma porteira Revenda, em ordem de data. */
export function recebimentosDaPorteira(e: EstadoDePagamentos, fichaId: string, destinoId: string): Recebimento[] {
  return e.recebimentos.filter((x) => x.fichaId === fichaId && x.destinoId === destinoId)
    .sort((a, b) => (a.recebidoEm + a.lancadoEm).localeCompare(b.recebidoEm + b.lancadoEm))
}

export function lancarRecebimento(x: Pick<Recebimento, 'fichaId' | 'destinoId' | 'valor' | 'recebidoEm' | 'obs'>): string {
  const id = novoId()
  mudar((e) => {
    e.recebimentos.push({ ...x, id, lancadoEm: agora(), anuladoEm: null, anuladoMotivo: '', comprovantes: [], lancadoPorNome: lerDono()?.nome ?? '' })
    e.pendentes.recebimentos = juntar(e.pendentes.recebimentos, id)
  })
  return id
}

export function anularRecebimento(id: string, motivo: string): void {
  mudar((e) => {
    const x = e.recebimentos.find((y) => y.id === id)
    if (!x || x.anuladoEm || x.recusado) return
    x.anuladoEm = agora()
    x.anuladoMotivo = motivo
    x.anuladoPorNome = lerDono()?.nome ?? ''
    e.pendentes.anulacoesRecebimento = juntar(e.pendentes.anulacoesRecebimento, id)
  })
}

/** Comprovante do recebimento: fica no IndexedDB deste aparelho e sobe ao R2 depois do recebimento (2G). */
export function anexarNoRecebimento(id: string, novos: Comprovante[]): void {
  mudar((e) => {
    const x = e.recebimentos.find((y) => y.id === id)
    if (x) x.comprovantes.push(...novos)
  })
}

/**
 * Só o gestor chega aqui (a tela não mostra para os outros, e o banco recusa).
 * R2B: `beneficiario` vazio = o comprador que pesou (acerto do extrato); um
 * nome = um corretor, pago fora do acerto.
 */
export function lancarComissao(fichaId: string, porCabeca: number, obs: string, beneficiario = ''): void {
  mudar((e) => {
    // acertada com o comprador não muda mais (o banco recusaria): a tela nem oferece
    if (e.comissoes[fichaId]?.acertoId) return
    e.comissoes[fichaId] = { fichaId, porCabeca, obs, lancadaEm: agora(), ...(beneficiario ? { beneficiario } : {}) }
    e.pendentes.comissoes = juntar(e.pendentes.comissoes, fichaId)
  })
}

export function lancarQuitacao(q: Omit<Quitacao, 'id' | 'lancadaEm' | 'anuladaEm' | 'anuladaMotivo'>): string {
  const id = novoId()
  mudar((e) => {
    // R1A: a baixa fica no nome de quem lançou (gestor ou Consulta)
    e.quitacoes.push({ ...q, id, lancadaEm: agora(), anuladaEm: null, anuladaMotivo: '', lancadaPorNome: lerDono()?.nome ?? '' })
    e.pendentes.quitacoes = juntar(e.pendentes.quitacoes, id)
  })
  return id
}

export function anularQuitacao(id: string, motivo: string): void {
  mudar((e) => {
    const q = e.quitacoes.find((x) => x.id === id)
    if (!q || q.anuladaEm || q.recusada) return
    q.anuladaEm = agora()
    q.anuladaMotivo = motivo
    q.anuladaPorNome = lerDono()?.nome ?? ''
    e.pendentes.anulacoes = juntar(e.pendentes.anulacoes, id)
  })
}

/** Comprovante do pagamento: o arquivo fica no IndexedDB deste aparelho (até a 2G). */
export function anexarNaQuitacao(id: string, novos: Comprovante[]): void {
  mudar((e) => {
    const q = e.quitacoes.find((x) => x.id === id)
    if (q) q.comprovantes.push(...novos)
  })
}

/* ---------- sincronizar (chamado pela fila, depois das fichas) ---------- */

interface Placar { subiram: number; desceram: number; recusas: string[] }

interface PagamentoComissaoDoBanco {
  id: string; ficha_id: string; valor: number; pago_em: string; obs: string; conta_origem?: string | null
  lancado_em: string; anulado_em: string | null; anulado_motivo: string
  lancado_por_nome?: string | null; anulado_por_nome?: string | null
}
interface QuitacaoDoBanco {
  conta_origem?: string | null
  id: string; ficha_id: string; parcela_id: string; valor: number; pago_em: string; obs: string
  lancada_em: string; anulada_em: string | null; anulada_motivo: string
  /** R1A: nomes de quem lançou e de quem anulou (vazio para o comprador, que não vê a equipe) */
  lancada_por_nome?: string | null; anulada_por_nome?: string | null
}
interface ComissaoDoBanco { ficha_id: string; por_cabeca: number; obs: string; lancada_em: string; acerto_id?: string | null; cabecas?: number | null; beneficiario?: string | null }
interface ComissaoClienteDoBanco { ficha_id: string; destino_id: string; por_cabeca: number; obs: string; lancada_em: string }
interface VendaDoBanco { ficha_id: string; destino_id: string; lotes: LoteDeVenda[]; obs: string; lancada_em: string; valor_total?: number | null }
interface RecebimentoDoBanco {
  id: string; ficha_id: string; destino_id: string; valor: number; recebido_em: string; obs: string
  lancado_em: string; anulado_em: string | null; anulado_motivo: string
  lancado_por_nome?: string | null; anulado_por_nome?: string | null
}

/** «a ficha / o pagamento ainda não chegou ao banco»: fica na fila, sem alarde */
function esperar(e: unknown, fichaId: string): boolean {
  if (!(e instanceof Recusa && e.status === 409
    && ['ficha_ausente', 'quitacao_ausente', 'recebimento_ausente', 'baixa_ausente', 'comissao_ausente'].includes(String(e.dados?.['erro'] ?? '')))) return false
  // R1A · contador preso: a ficha foi RECUSADA pelo servidor neste aparelho —
  // ela nunca vai chegar ao banco, então esperar seria esperar para sempre
  return !fichaRecusadaAqui(fichaId)
}

/** A frase da recusa para a lista do «Sincronizar» (nunca o código interno). */
function frase(e: unknown): string {
  if (!(e instanceof Recusa)) return 'não deu para concluir.'
  if (e.dados?.['erro'] === 'ficha_ausente') return 'a ficha foi recusada pelo servidor e não vai chegar ao banco.'
  return String(e.dados?.['mensagem'] ?? e.message)
}

/** A ficha está neste aparelho marcada «recusada pelo servidor» (fora da fila, não vai subir). */
export function fichaRecusadaAqui(fichaId: string | null | undefined): boolean {
  if (!fichaId) return false
  const e = lerEstado()
  return fichaId in e.recusadas.fichas && !e.pendentes.fichas.includes(fichaId)
}

/**
 * Recusa definitiva: o servidor entendeu e disse não, e diria não de novo.
 * Não contam a espera acima, o 401 (crachá vencido: entra de novo e manda) e
 * os 5xx (servidor ou banco fora do ar: tenta no próximo Sincronizar).
 */
function deVez(e: unknown, fichaId: string): e is Recusa {
  return e instanceof Recusa && !esperar(e, fichaId) && e.status >= 400 && e.status < 500 && e.status !== 401
}

/**
 * 3B · a ficha do banco ainda não tem a porteira de cliente: se a versão deste
 * aparelho ainda vai subir (pendente ou em conflito), espera; senão é recusa
 * de vez (a porteira saiu da ficha) e sai da fila.
 */
function porteiraAindaVai(e: unknown, fichaId: string): boolean {
  if (!(e instanceof Recusa) || e.status !== 409 || e.dados?.['erro'] !== 'destino_ausente') return false
  const est = lerEstado()
  return est.pendentes.fichas.includes(fichaId) || est.conflitos.some((c) => c.local.id === fichaId)
}

/** «ficha 2026-0012» para a frase da recusa; só «ficha», se ela não está neste aparelho. */
function nomeDaFicha(fichaId: string): string {
  const f = lerEstado().fichas.find((x) => x.id === fichaId)
  return f ? `ficha ${f.numero}` : 'ficha'
}

export async function sincronizarPagamentos(r: Placar): Promise<void> {
  // 1. sobe — quitação antes da anulação dela
  for (const fichaId of [...lerPagamentos().pendentes.comissoes]) {
    const c = lerPagamentos().comissoes[fichaId]
    if (!c) { tirar('comissoes', fichaId); continue }
    try {
      await api('comissoes', { ficha_id: fichaId, por_cabeca: c.porCabeca, obs: c.obs, beneficiario: c.beneficiario ?? '' })
      // só sai da fila se não mudou enquanto subia
      mudar((e) => { if (e.comissoes[fichaId]?.lancadaEm === c.lancadaEm) e.pendentes.comissoes = e.pendentes.comissoes.filter((x) => x !== fichaId) })
      r.subiram++
    } catch (e) {
      if (esperar(e, fichaId)) continue
      if (deVez(e, fichaId)) {
        // sai da fila e volta ao valor do banco: some daqui e a próxima descida vem inteira
        mudar((x) => {
          // corrigida de novo enquanto subia: a correção nova tenta no próximo Sincronizar
          if (x.comissoes[fichaId]?.lancadaEm !== c.lancadaEm) return
          x.pendentes.comissoes = x.pendentes.comissoes.filter((y) => y !== fichaId)
          delete x.comissoes[fichaId]
          x.ultimaSync = null
        })
        r.recusas.push(`Comissão de ${reais(c.porCabeca)}/cab na ${nomeDaFicha(fichaId)} não entrou: ${frase(e)}`)
        continue
      }
      if (e instanceof Recusa) { r.recusas.push(`Comissão: ${frase(e)}`); continue }
      throw e
    }
  }
  // 3B · comissão a receber (cliente): depois das fichas, como a comissão a pagar
  for (const k of [...lerPagamentos().pendentes.comissoesCliente]) {
    const c = lerPagamentos().comissoesCliente[k]
    if (!c) { tirar('comissoesCliente', k); continue }
    try {
      await api('comissoes-cliente', { ficha_id: c.fichaId, destino_id: c.destinoId, por_cabeca: c.porCabeca, obs: c.obs })
      mudar((e) => { if (e.comissoesCliente[k]?.lancadaEm === c.lancadaEm) e.pendentes.comissoesCliente = e.pendentes.comissoesCliente.filter((x) => x !== k) })
      r.subiram++
    } catch (e) {
      if (esperar(e, c.fichaId) || porteiraAindaVai(e, c.fichaId)) continue
      if (deVez(e, c.fichaId)) {
        // sai da fila e volta ao valor do banco (a próxima descida vem inteira)
        mudar((x) => {
          if (x.comissoesCliente[k]?.lancadaEm !== c.lancadaEm) return
          x.pendentes.comissoesCliente = x.pendentes.comissoesCliente.filter((y) => y !== k)
          delete x.comissoesCliente[k]
          x.ultimaSync = null
        })
        r.recusas.push(`Comissão do cliente de ${reais(c.porCabeca)}/cab na ${nomeDaFicha(c.fichaId)} não entrou: ${frase(e)}`)
        continue
      }
      if (e instanceof Recusa) { r.recusas.push(`Comissão do cliente: ${frase(e)}`); continue }
      throw e
    }
  }
  // 3F · tabela de venda da revenda: depois das fichas (a porteira tem de estar no banco)
  for (const k of [...lerPagamentos().pendentes.vendas]) {
    const v = lerPagamentos().vendas[k]
    if (!v) { tirar('vendas', k); continue }
    try {
      await api('vendas-revenda', { ficha_id: v.fichaId, destino_id: v.destinoId, lotes: v.lotes, obs: v.obs })
      mudar((e) => { if (e.vendas[k]?.lancadaEm === v.lancadaEm) e.pendentes.vendas = e.pendentes.vendas.filter((x) => x !== k) })
      r.subiram++
    } catch (e) {
      if (esperar(e, v.fichaId) || porteiraAindaVai(e, v.fichaId)) continue
      if (deVez(e, v.fichaId)) {
        // sai da fila e volta ao que o banco tem (a próxima descida vem inteira)
        mudar((x) => {
          if (x.vendas[k]?.lancadaEm !== v.lancadaEm) return
          x.pendentes.vendas = x.pendentes.vendas.filter((y) => y !== k)
          delete x.vendas[k]
          x.ultimaSync = null
        })
        r.recusas.push(`Preço de venda na ${nomeDaFicha(v.fichaId)} não entrou: ${frase(e)}`)
        continue
      }
      if (e instanceof Recusa) { r.recusas.push(`Preço de venda: ${frase(e)}`); continue }
      throw e
    }
  }
  // 3F · recebimentos da revenda e as anulações deles (como a quitação)
  for (const id of [...lerPagamentos().pendentes.recebimentos]) {
    const x = lerPagamentos().recebimentos.find((y) => y.id === id)
    if (!x) { tirar('recebimentos', id); continue }
    try {
      await api('recebimentos-revenda', { id, ficha_id: x.fichaId, destino_id: x.destinoId, valor: x.valor, recebido_em: x.recebidoEm, obs: x.obs })
      tirar('recebimentos', id)
      r.subiram++
    } catch (e) {
      if (esperar(e, x.fichaId) || porteiraAindaVai(e, x.fichaId)) continue
      if (deVez(e, x.fichaId)) {
        // nunca entrou no banco: fica aqui riscado (com os comprovantes), fora do «recebido»
        mudar((z) => {
          const y = z.recebimentos.find((w) => w.id === id)
          if (y) y.recusado = frase(e)
          z.pendentes.recebimentos = z.pendentes.recebimentos.filter((w) => w !== id)
          z.pendentes.anulacoesRecebimento = z.pendentes.anulacoesRecebimento.filter((w) => w !== id)
        })
        r.recusas.push(`Recebimento de ${reais(x.valor)} na ${nomeDaFicha(x.fichaId)} não entrou: ${frase(e)}`)
        continue
      }
      if (e instanceof Recusa) { r.recusas.push(`Recebimento: ${frase(e)}`); continue }
      throw e
    }
  }
  for (const id of [...lerPagamentos().pendentes.anulacoesRecebimento]) {
    const x = lerPagamentos().recebimentos.find((y) => y.id === id)
    if (!x) { tirar('anulacoesRecebimento', id); continue }
    if (lerPagamentos().pendentes.recebimentos.includes(id)) continue // o recebimento ainda não subiu
    try {
      await api('recebimentos-revenda/anular', { id, motivo: x.anuladoMotivo })
      tirar('anulacoesRecebimento', id)
      r.subiram++
    } catch (e) {
      if (esperar(e, x.fichaId)) continue
      if (deVez(e, x.fichaId)) {
        // a anulação não valeu: o recebimento volta a valer aqui
        mudar((z) => {
          const y = z.recebimentos.find((w) => w.id === id)
          if (y) { y.anuladoEm = null; y.anuladoMotivo = '' }
          z.pendentes.anulacoesRecebimento = z.pendentes.anulacoesRecebimento.filter((w) => w !== id)
          z.ultimaSync = null
        })
        r.recusas.push(`Anulação do recebimento de ${reais(x.valor)} na ${nomeDaFicha(x.fichaId)} não valeu: ${frase(e)}`)
        continue
      }
      if (e instanceof Recusa) { r.recusas.push(`Anulação do recebimento: ${frase(e)}`); continue }
      throw e
    }
  }
  // R2B · as baixas da 014 (depois das comissões: o pagamento precisa da comissão no banco)
  await subirBaixas014(r)
  for (const id of [...lerPagamentos().pendentes.quitacoes]) {
    const q = lerPagamentos().quitacoes.find((x) => x.id === id)
    if (!q) { tirar('quitacoes', id); continue }
    try {
      await api('quitacoes', { id, ficha_id: q.fichaId, parcela_id: q.parcelaId, valor: q.valor, pago_em: q.pagoEm, obs: q.obs, conta_origem: q.contaOrigem ?? '' })
      tirar('quitacoes', id)
      r.subiram++
    } catch (e) {
      if (esperar(e, q.fichaId)) continue
      if (deVez(e, q.fichaId)) {
        // nunca entrou no banco: fica aqui riscada (com os comprovantes), fora do «pago»;
        // a anulação dela, se havia, não tem mais o que anular
        mudar((x) => {
          const y = x.quitacoes.find((z) => z.id === id)
          if (y) y.recusada = frase(e)
          x.pendentes.quitacoes = x.pendentes.quitacoes.filter((z) => z !== id)
          x.pendentes.anulacoes = x.pendentes.anulacoes.filter((z) => z !== id)
        })
        r.recusas.push(`Pagamento de ${reais(q.valor)} na ${nomeDaFicha(q.fichaId)} não entrou: ${frase(e)}`)
        continue
      }
      if (e instanceof Recusa) { r.recusas.push(`Pagamento: ${frase(e)}`); continue }
      throw e
    }
  }
  for (const id of [...lerPagamentos().pendentes.anulacoes]) {
    const q = lerPagamentos().quitacoes.find((x) => x.id === id)
    if (!q) { tirar('anulacoes', id); continue }
    if (lerPagamentos().pendentes.quitacoes.includes(id)) continue // a quitação ainda não subiu
    try {
      await api('quitacoes/anular', { id, motivo: q.anuladaMotivo })
      tirar('anulacoes', id)
      r.subiram++
    } catch (e) {
      if (esperar(e, q.fichaId)) continue
      if (deVez(e, q.fichaId)) {
        // a anulação não valeu: o pagamento volta a valer aqui, e a próxima
        // descida vem inteira para trazer o que o banco tem de fato
        mudar((x) => {
          const y = x.quitacoes.find((z) => z.id === id)
          if (y) { y.anuladaEm = null; y.anuladaMotivo = '' }
          x.pendentes.anulacoes = x.pendentes.anulacoes.filter((z) => z !== id)
          x.ultimaSync = null
        })
        r.recusas.push(`Anulação do pagamento de ${reais(q.valor)} na ${nomeDaFicha(q.fichaId)} não valeu: ${frase(e)}`)
        continue
      }
      if (e instanceof Recusa) { r.recusas.push(`Anulação: ${frase(e)}`); continue }
      throw e
    }
  }

  // 2. desce
  const desde = lerPagamentos().ultimaSync
  const resp = await api<{
    agora: string; quitacoes: QuitacaoDoBanco[]; comissoes?: ComissaoDoBanco[]; comissoes_cliente?: ComissaoClienteDoBanco[]
    vendas_revenda?: VendaDoBanco[]; recebimentos_revenda?: RecebimentoDoBanco[]
    recebimentos_comissao?: RecebimentoDoBanco[]; pagamentos_comissao?: PagamentoComissaoDoBanco[]
  }>(
    `pagamentos${desde ? `?desde=${encodeURIComponent(desde)}` : ''}`)
  mudar((e) => {
    for (const l of resp.quitacoes) {
      const i = e.quitacoes.findIndex((x) => x.id === l.id)
      const local = i >= 0 ? e.quitacoes[i]! : undefined
      // anulação feita aqui e ainda não enviada vale mais que a versão do banco
      const anulacaoLocal = local && e.pendentes.anulacoes.includes(l.id)
      const nova: Quitacao = {
        id: l.id, fichaId: l.ficha_id, parcelaId: l.parcela_id, valor: Number(l.valor), pagoEm: l.pago_em, obs: l.obs,
        lancadaEm: String(l.lancada_em),
        anuladaEm: anulacaoLocal ? local!.anuladaEm : l.anulada_em ? String(l.anulada_em) : null,
        anuladaMotivo: anulacaoLocal ? local!.anuladaMotivo : l.anulada_motivo,
        // os comprovantes ficam no aparelho de quem anexou (até a 2G)
        comprovantes: local?.comprovantes ?? [],
        lancadaPorNome: l.lancada_por_nome || local?.lancadaPorNome || '',
        anuladaPorNome: anulacaoLocal ? local!.anuladaPorNome : (l.anulada_por_nome || local?.anuladaPorNome || ''),
        ...((l.conta_origem || local?.contaOrigem) ? { contaOrigem: l.conta_origem || local?.contaOrigem } : {}),
      }
      if (!local || local.anuladaEm !== nova.anuladaEm) r.desceram++
      if (i >= 0) e.quitacoes[i] = nova
      else e.quitacoes.push(nova)
    }
    if (resp.comissoes === undefined) {
      // o servidor não entrega comissão a este papel: nada dela fica no aparelho
      e.comissoes = {}
      e.pendentes.comissoes = []
      e.comissoesCliente = {}
      e.pendentes.comissoesCliente = []
      // 3F: nem o preço de venda nem os recebimentos ficam no aparelho do comprador
      e.vendas = {}
      e.recebimentos = []
      e.pendentes.vendas = []
      e.pendentes.recebimentos = []
      e.pendentes.anulacoesRecebimento = []
      // R2B: nem o recebimento nem o pagamento da comissão
      e.recebimentosComissao = []
      e.pagamentosComissao = []
      e.pendentes.recebimentosComissao = []
      e.pendentes.anulacoesRecebimentoComissao = []
      e.pendentes.pagamentosComissao = []
      e.pendentes.anulacoesPagamentoComissao = []
    } else {
      for (const l of resp.comissoes) {
        if (e.pendentes.comissoes.includes(l.ficha_id)) continue // a mudança daqui ainda vai subir
        const antes = e.comissoes[l.ficha_id]
        if (!antes || antes.porCabeca !== Number(l.por_cabeca) || antes.obs !== l.obs
            || (antes.acertoId ?? null) !== (l.acerto_id ?? null)) r.desceram++
        e.comissoes[l.ficha_id] = {
          fichaId: l.ficha_id, porCabeca: Number(l.por_cabeca), obs: l.obs, lancadaEm: String(l.lancada_em), acertoId: l.acerto_id ?? null,
          cabecas: l.cabecas === null || l.cabecas === undefined ? null : Number(l.cabecas),
          ...(l.beneficiario ? { beneficiario: l.beneficiario } : {}),
        }
      }
      // 3B: sem a chave (migração 010 ainda não rodou), fica o que está aqui
      for (const l of resp.comissoes_cliente ?? []) {
        const k = chaveDoCliente(l.ficha_id, l.destino_id)
        if (e.pendentes.comissoesCliente.includes(k)) continue // a mudança daqui ainda vai subir
        const antes = e.comissoesCliente[k]
        if (!antes || antes.porCabeca !== Number(l.por_cabeca) || antes.obs !== l.obs) r.desceram++
        e.comissoesCliente[k] = { fichaId: l.ficha_id, destinoId: l.destino_id, porCabeca: Number(l.por_cabeca), obs: l.obs, lancadaEm: String(l.lancada_em) }
      }
      // 3F: sem as chaves (migração 012 ainda não rodou), fica o que está aqui
      for (const l of resp.vendas_revenda ?? []) {
        const k = chaveDoCliente(l.ficha_id, l.destino_id)
        if (e.pendentes.vendas.includes(k)) continue // a mudança daqui ainda vai subir
        const lotes = Array.isArray(l.lotes) ? l.lotes : []
        const antes = e.vendas[k]
        const valorTotal = l.valor_total === null || l.valor_total === undefined ? null : Number(l.valor_total)
        if (!antes || JSON.stringify(antes.lotes) !== JSON.stringify(lotes) || antes.obs !== l.obs || (antes.valorTotal ?? null) !== valorTotal) r.desceram++
        e.vendas[k] = { fichaId: l.ficha_id, destinoId: l.destino_id, lotes, obs: l.obs, lancadaEm: String(l.lancada_em), valorTotal }
      }
      for (const l of resp.recebimentos_revenda ?? []) {
        const i = e.recebimentos.findIndex((x) => x.id === l.id)
        const local = i >= 0 ? e.recebimentos[i]! : undefined
        // anulação feita aqui e ainda não enviada vale mais que a versão do banco
        const anulacaoLocal = local && e.pendentes.anulacoesRecebimento.includes(l.id)
        const novo: Recebimento = {
          id: l.id, fichaId: l.ficha_id, destinoId: l.destino_id, valor: Number(l.valor), recebidoEm: l.recebido_em, obs: l.obs,
          lancadoEm: String(l.lancado_em),
          anuladoEm: anulacaoLocal ? local!.anuladoEm : l.anulado_em ? String(l.anulado_em) : null,
          anuladoMotivo: anulacaoLocal ? local!.anuladoMotivo : l.anulado_motivo,
          comprovantes: local?.comprovantes ?? [],
          lancadoPorNome: l.lancado_por_nome || local?.lancadoPorNome || '',
          anuladoPorNome: anulacaoLocal ? local!.anuladoPorNome : (l.anulado_por_nome || local?.anuladoPorNome || ''),
        }
        if (!local || local.anuladoEm !== novo.anuladoEm) r.desceram++
        if (i >= 0) e.recebimentos[i] = novo
        else e.recebimentos.push(novo)
      }
      // R2B: sem as chaves (a 014 ainda não rodou), fica o que está aqui
      for (const l of resp.recebimentos_comissao ?? []) {
        const i = e.recebimentosComissao.findIndex((x) => x.id === l.id)
        const local = i >= 0 ? e.recebimentosComissao[i]! : undefined
        const anulacaoLocal = local && e.pendentes.anulacoesRecebimentoComissao.includes(l.id)
        const novo: Recebimento = {
          id: l.id, fichaId: l.ficha_id, destinoId: l.destino_id, valor: Number(l.valor), recebidoEm: l.recebido_em, obs: l.obs,
          lancadoEm: String(l.lancado_em),
          anuladoEm: anulacaoLocal ? local!.anuladoEm : l.anulado_em ? String(l.anulado_em) : null,
          anuladoMotivo: anulacaoLocal ? local!.anuladoMotivo : l.anulado_motivo,
          comprovantes: local?.comprovantes ?? [],
          lancadoPorNome: l.lancado_por_nome || local?.lancadoPorNome || '',
          anuladoPorNome: anulacaoLocal ? local!.anuladoPorNome : (l.anulado_por_nome || local?.anuladoPorNome || ''),
        }
        if (!local || local.anuladoEm !== novo.anuladoEm) r.desceram++
        if (i >= 0) e.recebimentosComissao[i] = novo
        else e.recebimentosComissao.push(novo)
      }
      for (const l of resp.pagamentos_comissao ?? []) {
        const i = e.pagamentosComissao.findIndex((x) => x.id === l.id)
        const local = i >= 0 ? e.pagamentosComissao[i]! : undefined
        const anulacaoLocal = local && e.pendentes.anulacoesPagamentoComissao.includes(l.id)
        const novo: PagamentoDeComissao = {
          id: l.id, fichaId: l.ficha_id, valor: Number(l.valor), pagoEm: l.pago_em, obs: l.obs, contaOrigem: l.conta_origem ?? '',
          lancadoEm: String(l.lancado_em),
          anuladoEm: anulacaoLocal ? local!.anuladoEm : l.anulado_em ? String(l.anulado_em) : null,
          anuladoMotivo: anulacaoLocal ? local!.anuladoMotivo : l.anulado_motivo,
          comprovantes: local?.comprovantes ?? [],
          lancadoPorNome: l.lancado_por_nome || local?.lancadoPorNome || '',
          anuladoPorNome: anulacaoLocal ? local!.anuladoPorNome : (l.anulado_por_nome || local?.anuladoPorNome || ''),
        }
        if (!local || local.anuladoEm !== novo.anuladoEm) r.desceram++
        if (i >= 0) e.pagamentosComissao[i] = novo
        else e.pagamentosComissao.push(novo)
      }
    }
    e.ultimaSync = resp.agora || e.ultimaSync
  })
}

/**
 * R1A · «Sair»: fica só o que ainda não subiu (nada se perde); comissões,
 * pagamentos, preço de venda e recebimentos que já estão no banco saem do
 * aparelho. A próxima descida vem inteira.
 */
export function enxugarPagamentosAoSair(): void {
  garantirDono()
  if (!donoAberto) return
  mudar((e) => {
    const p = e.pendentes
    e.comissoes = Object.fromEntries(Object.entries(e.comissoes).filter(([k]) => p.comissoes.includes(k)))
    e.comissoesCliente = Object.fromEntries(Object.entries(e.comissoesCliente).filter(([k]) => p.comissoesCliente.includes(k)))
    e.vendas = Object.fromEntries(Object.entries(e.vendas).filter(([k]) => p.vendas.includes(k)))
    e.quitacoes = e.quitacoes.filter((q) => p.quitacoes.includes(q.id) || p.anulacoes.includes(q.id))
    e.recebimentos = e.recebimentos.filter((x) => p.recebimentos.includes(x.id) || p.anulacoesRecebimento.includes(x.id))
    e.recebimentosComissao = e.recebimentosComissao.filter((x) => p.recebimentosComissao.includes(x.id) || p.anulacoesRecebimentoComissao.includes(x.id))
    e.pagamentosComissao = e.pagamentosComissao.filter((x) => p.pagamentosComissao.includes(x.id) || p.anulacoesPagamentoComissao.includes(x.id))
    e.ultimaSync = null
  })
}

function tirar(tipo: keyof EstadoDePagamentos['pendentes'], id: string): void {
  mudar((e) => { e.pendentes[tipo] = e.pendentes[tipo].filter((x) => x !== id) })
}

/**
 * R2B · Sobe o recebimento da comissão do cliente e o pagamento da comissão
 * fora do acerto (014), e as anulações deles — a mesma regra do recebimento
 * da revenda: espera a ficha (e a comissão) chegar; recusa de vez fica aqui
 * riscada, fora das somas; anulação recusada volta a valer.
 */
async function subirBaixas014(r: Placar): Promise<void> {
  type Item = { id: string; fichaId: string; valor: number; recusado?: string; anuladoEm: string | null; anuladoMotivo: string }
  const tipos = [
    {
      lista: (e: EstadoDePagamentos): Item[] => e.recebimentosComissao, pend: 'recebimentosComissao' as const, anul: 'anulacoesRecebimentoComissao' as const,
      rota: 'recebimentos-comissao', nome: 'Recebimento da comissão',
      corpo: (x: Item) => { const y = x as unknown as Recebimento; return { id: y.id, ficha_id: y.fichaId, destino_id: y.destinoId, valor: y.valor, recebido_em: y.recebidoEm, obs: y.obs } },
    },
    {
      lista: (e: EstadoDePagamentos): Item[] => e.pagamentosComissao, pend: 'pagamentosComissao' as const, anul: 'anulacoesPagamentoComissao' as const,
      rota: 'pagamentos-comissao', nome: 'Pagamento da comissão',
      corpo: (x: Item) => { const y = x as unknown as PagamentoDeComissao; return { id: y.id, ficha_id: y.fichaId, valor: y.valor, pago_em: y.pagoEm, obs: y.obs, conta_origem: y.contaOrigem ?? '' } },
    },
  ]
  for (const t of tipos) {
    for (const id of [...lerPagamentos().pendentes[t.pend]]) {
      const x = t.lista(lerPagamentos()).find((y) => y.id === id)
      if (!x) { tirar(t.pend, id); continue }
      try {
        await api(t.rota, t.corpo(x))
        tirar(t.pend, id)
        r.subiram++
      } catch (e) {
        if (esperar(e, x.fichaId) || porteiraAindaVai(e, x.fichaId)) continue
        if (e instanceof Recusa && e.status === 503) continue // a 014 ainda não rodou: fica guardado
        if (deVez(e, x.fichaId)) {
          mudar((z) => {
            const y = t.lista(z).find((w) => w.id === id)
            if (y) y.recusado = frase(e)
            z.pendentes[t.pend] = z.pendentes[t.pend].filter((w) => w !== id)
            z.pendentes[t.anul] = z.pendentes[t.anul].filter((w) => w !== id)
          })
          r.recusas.push(`${t.nome} de ${reais(x.valor)} na ${nomeDaFicha(x.fichaId)} não entrou: ${frase(e)}`)
          continue
        }
        if (e instanceof Recusa) { r.recusas.push(`${t.nome}: ${frase(e)}`); continue }
        throw e
      }
    }
    for (const id of [...lerPagamentos().pendentes[t.anul]]) {
      const x = t.lista(lerPagamentos()).find((y) => y.id === id)
      if (!x) { tirar(t.anul, id); continue }
      if (lerPagamentos().pendentes[t.pend].includes(id)) continue // o lançamento ainda não subiu
      try {
        await api(`${t.rota}/anular`, { id, motivo: x.anuladoMotivo })
        tirar(t.anul, id)
        r.subiram++
      } catch (e) {
        if (esperar(e, x.fichaId)) continue
        if (e instanceof Recusa && e.status === 503) continue
        if (deVez(e, x.fichaId)) {
          mudar((z) => {
            const y = t.lista(z).find((w) => w.id === id)
            if (y) { y.anuladoEm = null; y.anuladoMotivo = '' }
            z.pendentes[t.anul] = z.pendentes[t.anul].filter((w) => w !== id)
            z.ultimaSync = null
          })
          r.recusas.push(`Anulação (${t.nome.toLowerCase()}) de ${reais(x.valor)} na ${nomeDaFicha(x.fichaId)} não valeu: ${frase(e)}`)
          continue
        }
        if (e instanceof Recusa) { r.recusas.push(`Anulação: ${frase(e)}`); continue }
        throw e
      }
    }
  }
}
